import { cloneElement, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { SIZE_TYPE_200, SIZE_TYPE_150_HANJIN } from "@/utils/Print";

export default function PrintPortal({ children, sizeType }) {
  const container = useRef(document.createElement("div"));
  const externalWindow = useRef(null);

  const labelMarginSize = () => {
    switch (sizeType) {
      case SIZE_TYPE_200:
        return "margin-top: 5.5mm !important;";
      default:
        return "margin-top: 2.4mm !important;";
    }
  };

  const labelSize = () => {
    switch (sizeType) {
      case SIZE_TYPE_200:
        return "width: 200mm; height: 100mm;";
      case SIZE_TYPE_150_HANJIN:
        return "width: 100mm; height: 150mm;";
      default:
        return "width: 100mm; height: 6in;";
    }
  };

  const pageSize = () => {
    switch (sizeType) {
      case SIZE_TYPE_200:
        return "size: 200mm 100mm landscape;";
      case SIZE_TYPE_150_HANJIN:
        return "size: 100mm 150mm;";
      default:
        return "size: 100mm 6in;";
    }
  };

  const paperSize = () => {
    switch (sizeType) {
      case SIZE_TYPE_200:
        return "print and (width: 100mm) and (height: 200mm) ";
      default:
        return "100mm 6in";
    }
  };

  const buildCss = () => {
    if (sizeType === SIZE_TYPE_150_HANJIN) {
      return `@media print {
      @page { ${pageSize()} margin: 0; padding: 0; }
      html, body {
        margin: 0;
        padding: 0;
        width: auto;
        height: auto;
      }
      .label {
        ${labelSize()}
        position: relative;
        overflow: hidden;
        clip-path: inset(0);
        box-sizing: border-box;
        page-break-after: always;
        break-after: page;
        page-break-inside: avoid;
        break-inside: avoid;
      }
      .label:last-child {
        page-break-after: auto;
        break-after: auto;
      }
    }

    html, body { margin: 0; padding: 0; display: block; }
    .label {
      ${labelSize()}
      position: relative;
      overflow: hidden;
      clip-path: inset(0);
      box-sizing: border-box;
    }
    `;
    }

    return `@media ${paperSize()} {
      @page { ${pageSize()} margin: 0; padding: 0; }
    }
  
    html, body { margin: 0; padding: 0; display: grid; }
      .label { ${labelSize()} position: relative; overflow: hidden; }
      // .label:not(:first-of-type) { ${labelMarginSize()} }
    `;
  };

  useEffect(() => {
    const win = window.open("", "", "width=1200,height=1200,left=200,top=200");

    if (!win) {
      window.alert(
        "운송장 출력 창을 여는 데 실패했습니다.\n팝업 차단을 해제했는지 확인하시고, 운송장 출력을 다시 시도해주세요.",
      );
      return;
    }

    externalWindow.current = win;

    const css = buildCss();

    // 부모 창(document)이 아니라 새로 연 창(win.document)의 노드를 사용해야
    // cross-document 문제 없이 안전하게 붙는다.
    const head =
      win.document.head || win.document.getElementsByTagName("head")[0];
    const style = win.document.createElement("style");
    head.appendChild(style);
    style.type = "text/css";
    style.appendChild(win.document.createTextNode(css));

    win.document.body.appendChild(container.current);
    win.onafterprint = () => {
      setTimeout(() => {
        win.close();
      }, 200);
    };

    return () => {
      if (externalWindow.current) {
        externalWindow.current.close();
        externalWindow.current = null;
      }
    };
  }, []);

  const handleLoaded = useCallback(() => {
    if (externalWindow.current) {
      externalWindow.current.print();
    }
  }, []);

  return createPortal(
    cloneElement(children, {
      onLoaded: handleLoaded,
    }),
    container.current,
  );
}
